import { Box, IconButton, makeStyles, styled } from '@material-ui/core';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import { Collection, Events } from '@type-r/models';
import { useLinked } from '@type-r/react';
import { DigitizedIcon } from 'common/invoice-aging';
import { AppLink, asCurrency, asTimelessDate, Button, div, Flex, FormCheckbox, isEmbeddedApp, Link, Loading, PopoverValue, PopupDialog, PopupIODialog, ReadOnly } from 'lqx-react';
import * as React from 'react';
import { useContext } from 'react';
import { execUnmatchInvoicesForMultiplePaymnets, RecsInvoice } from 'src/app/pages/inmatch/api';
import { MatchLabel, PaymentInfoIcon, RemittanceAdviceIcon, RowH2Bold, SmallChip } from '../../../../../../common/inmatch';
import { Can } from 'src/app/services/can';
import { UserEntitlementCatalogs } from 'src/app/models/UserEntitlements';

export const InvoiceTile = ( getPayment: Function, selected : Collection<RecsInvoice>, isItemLoaded? : (index: number) => boolean  ) =>
({ data, style, index }) => {
    const invoice : RecsInvoice = data.items[ index ],
        matched = invoice && invoice.paymentMatchedId && getPayment(invoice.paymentMatchedId);

        const selectPayment = () => Events.trigger( 'InvoiceTile:selectPayment', invoice.paymentMatchedId );
    const readOnly = useContext(ReadOnly) || (!Can(UserEntitlementCatalogs.BankEntryWrite) && !Can(UserEntitlementCatalogs.BankEntryConfirm));
    const $isOpen = useLinked(false);

    const matchInvoice = () => {
        if (invoice.remittanceAdviceIds.length) {
            $isOpen.true();
            return;
        }

        Events.trigger( 'invoices:add', invoice )
    };

    const unmatchInvoice = async () => {
        const paymentInvoiceIds = [{ paymentId: matched.id, invoiceId: invoice.id }]
        await execUnmatchInvoicesForMultiplePaymnets(paymentInvoiceIds);
    };

    return (
        isItemLoaded && !isItemLoaded(index) ? <Loading /> : 
        <div style={style}>
            <Flex row>
                { readOnly ? null : <FormCheckbox $checked={ selected.$includes( invoice ) } /> }
                <InvoiceRow
                    icon={
                        <DigitizedIcon invoice={ invoice }>
                        {
                            matched
                                ? <MatchLabel onClick={ readOnly ? undefined : selectPayment } type={matched.originType}/>
                                : <UnmatchedIcon/>
                        }
                        </DigitizedIcon>
                    }
                    invoice={ invoice }
                    paymentId={ matched && !readOnly ? 
                        <Link onClick={ selectPayment }>
                            Select
                        </Link>
                    : null }
                    showCustomer
                    lighter
                    ><>
                        <ListField>{invoice.additionalReferenceIdentifier}</ListField>
                        <RAButton remittanceAdviceIds={invoice.remittanceAdviceIds} paymentInfoIds={invoice.paymentInfoIds} />
                        { readOnly ? null :
                            <>
                            { matched
                                    ? <LinkedButton onClick={ unmatchInvoice } blockAction={invoice.remittanceAdviceIds.length} />
                                    : <AddButton onClick={ matchInvoice } />
                            }
                            </>
                        }
                    </>
                </InvoiceRow>
            </Flex>
            { $isOpen.value ? <InvalidDialog $isOpen={$isOpen} action='matching' /> : undefined }
        </div>
    )
}

const MatchedIcon = () => <img src="/assets/images/green-match.png" />

export const SendToButton = ({ onClick }) =>
    <IconButton aria-label="Send To" size="small" onClick={ onClick }>
        <img src="/assets/images/send-to-dark.png" alt="Send To" />
    </IconButton>

export const RAButton = ({ remittanceAdviceIds, paymentInfoIds }) => {
    return ( remittanceAdviceIds.length || paymentInfoIds.length ?
        <PopoverValue anchorAlign="left" transformAlign="center" withCloseIcon={false} label={ 
            <IconButton aria-label='Remmitance Advice' size="small">
                <RemittanceAdviceIcon />
            </IconButton> }>

            { remittanceAdviceIds.length ?
            <>
                <h6>Customer Remittance Info</h6>
                {remittanceAdviceIds.map( id =>
                    <AppLink key={id} returnUrl href={`/remittance-advices/details/${id}`}>
                        {id}
                    </AppLink>
                )}
            </> : null }
            
            { paymentInfoIds.length ?
            <>
                <h6>Payment Info</h6>
                {paymentInfoIds.map( id =>
                    <AppLink key={id} returnUrl href={`/bank-transactions/details/${id}`}>
                        {id}
                    </AppLink>
                )}
            </> : null }
        </PopoverValue>
    :
        <IconButton aria-label='Not Valid Remmitance Advice' size="small" disabled>
            <RemittanceAdviceIcon invalid />
        </IconButton>)
}


const LinkedButton = ({ onClick, blockAction }) => {
    const $isOpen = useLinked( false );

    return (
        <>
        <IconButton aria-label="Unmatch" size="small"
            onClick={ $isOpen.true }
        >
            <LinkOffIcon />
        </IconButton>
        { blockAction && $isOpen.value ? <InvalidDialog $isOpen={$isOpen} action='unmatching' /> : undefined}
        { $isOpen.value && !blockAction ? <UnmatchConfirmationDialog $isOpen={$isOpen} onClick={onClick} /> : null }
        </>
    );
}

export const UnmatchConfirmationDialog = ({ $isOpen, onClick }) => 
    ConfirmationDialog({$isOpen, onClick, action: 'Unmatch'});

export const ConfirmationDialog = ({ $isOpen, onClick, action }) => {

    return (
        <PopupIODialog
            title={ `Are you sure you want to ${action === 'Match' ? 'match' : 'unmatch'}?` }
            $isOpen={ $isOpen }
            actionLabel="Yes"
            success={ action === 'Match' ? 'Matched' : 'Unmatched' }
            doIO={ onClick }
            hideTitleOnSuccess
        />
    );
};

const UnmatchedIcon = styled(
    ( props ) => <div {...props}>·</div>
)({
    color: '#FF274B',
    width: 14,
    cursor: 'default',
    height: 21,
    textAlign: 'center',
    fontWeight: 800,
})

const AddButton = ({ onClick, disabled = null }) =>
    <IconButton aria-label='Add Button' size="small" onClick={ onClick } disabled={ disabled }>
        <AddCircleOutlineIcon />
    </IconButton>


export const InvoiceRow = ({ icon, invoice, children, compact, middle, paymentId, showCustomer, lighter }:{
    icon : React.ReactNode
    invoice : RecsInvoice
    children : React.ReactNode
    compact? : boolean,
    middle? : React.ReactNode,
    paymentId? : any,
    showCustomer? : boolean,
    lighter? : boolean,
}) => {
    const classes = useStyles();
    return (<ListItem
        icon={ icon }
        title={ 
            <RowH2Bold className={ showCustomer ? classes.container : '' }>
                <span className={ showCustomer ? classes.span : '' } title={ invoice.customerName }>{invoice.invoiceNumber} {showCustomer ? `- ${invoice.customerName}` : ''}</span>
            </RowH2Bold> 
        }
        extensionBox={ <InvoiceDetails invoice={ invoice } paymentId={ paymentId }/>}
    >
        { middle }

        { compact ? null :
            <>
                { !middle ? 
                    <SmallChip>{invoice.age} {Math.abs(invoice.age) === 1 ? 'day' : 'days'}</SmallChip>
                : null }
                <ListField>{asCurrency( invoice.displayAmount, invoice.currency )}</ListField>
                <ListField>{ lighter ? 
                    <>{ asTimelessDate( invoice.invoiceDueDate ) }</> :
                    <RowH2Bold>{ asTimelessDate( invoice.invoiceDueDate ) }</RowH2Bold>
                    }
                </ListField> 
            </>
        }

        { children }
    </ListItem>)
}

export const SmallBox = div({
    padding: 8,
    boxShadow: '1px 0 2px 0 rgb(0 0 0 / 4%)',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: '#f9f9f9',
    alignItems: 'center',
    margin: 4
})

export const SmallInvoiceTile = ({ invoice, paymentId }) =>
    <SmallBox>
        <PaymentInfoIcon />
        <PopoverValue
            label={ <RowH2Bold>{invoice.invoiceNumber}</RowH2Bold> } 
            anchorAlign="left" 
            transformAlign="left"
        >
            <InvoiceDetails invoice={ invoice } paymentId={ paymentId }/>
        </PopoverValue>
        
        <FlexListField>{asCurrency( invoice.displayAmount, invoice.currency )}</FlexListField>
        <FlexListField>{ asTimelessDate( invoice.estimatedPayDate ) }</FlexListField>
    </SmallBox>

const InvoiceDetails = ({ invoice, paymentId }:{
    invoice : RecsInvoice,
    paymentId: string
}) => {

    return(
        <>
            <Box display="flex" flexDirection="row" p={2} pb={1}>
                <DataField label="legal entity" value={invoice.customerName}/>
                <DataField label="projected payment" value={asCurrency(invoice.unreconciledAmount, invoice.currency)}/>
                <DataField label="estimated value date" value="-"/>
            </Box> 
            <Box display="flex" flexDirection="row" p={2} pb={1}>
                <DataField label="projected pay date" value={asTimelessDate(invoice.estimatedPayDate)}/>
                <DataField label="inv due" value={asTimelessDate(invoice.invoiceDueDate)}/>

                { paymentId ? <DataField label="Linked Payment" value={paymentId}/> : null }
            </Box>
        </>
    )
}

export const ListItem = ( props : {
    title : React.ReactNode,
    icon  : React.ReactNode,
    extensionBox : JSX.Element | string,
    children : React.ReactNode
}) => {
    const classes = useStyles( props );
    return (
        <div className={ classes.root }>
            <div className={ classes.icon }>{ props.icon }</div>
            <PopoverValue label={ props.title } anchorAlign="left" transformAlign="left">
                { props.extensionBox }
            </PopoverValue>
            <Flex grow/>
            { props.children }
        </div>
    )
}

export const DataField = ({label, value} : {
    label: any,
    value: any
}) => {
    return (
        <Box display="flex" flexDirection="column" mr={2} my={0.5} minWidth={100}>
            <Label>
                {label}
            </Label>
            <Value>
                {value}
            </Value>
        </Box>
    );
};

export const FlexListField = styled('div')({
    paddingLeft: 8,
    paddingRight: 8,
    fontSize: 12,
    color: '#A0A0A0'
})

export const ListField = styled('div')({
    paddingLeft: 8,
    paddingRight: 8,
    minWidth: 120,
    fontSize: 12,
    color: '#A0A0A0'
})

export const Label = styled('span')({
    color: '#728A97',
    marginRight: '1em',
    fontFamily: 'Montserrat',
    fontSize: 10,
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
});

const Value = styled('div')({
    color: '#000',
    fontFamily: 'Montserrat',
    fontSize: 14,
    fontWeight: 500,
});

const useStyles = makeStyles({
    root : {
        padding: '0px 6px 0px 6px',
        display : 'flex',
        flex: '1 auto',
        flexDirection: 'row',
        alignItems: 'center',
        boxSizing: 'border-box',
        borderRadius: '0 2px 2px',
        margin: '2px 0 2px 0',
        backgroundColor: '#F9F9F9',
        border: '1px solid #F1F5F8',
        boxShadow: '1px 0 2px 0 rgba(0,0,0,0.04)',
/*
        '& button' : {
            visibility: 'hidden',
        },
        '&:hover button' : {
            visibility: 'visible',
        } */
    },

    icon : {
        margin: 6
    },
    container : {
        display: 'grid',
    },
    span : {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
})

export const InvalidDialog = ({ $isOpen, action, onClose = undefined }) => {

    return $isOpen.value && (
        <PopupDialog
            title={'Invalid operation'}
            actions={
                <>
                    <Button onClick={ (e) => { 
                        e.stopPropagation(); 
                        $isOpen.false(); 
                        if (onClose) 
                            onClose();
                    } }>Close</Button>
                </>
            }
        >
            {`Please try ${action} invoice(s) through Remittance Advice section.`}
        </PopupDialog>
    );
};
